// How a modal sends a request (submitOnce): once per click, the latch set in the click itself and released once the
// request settles, so a refused one can be corrected and sent again.
import { useRef } from "react";
import { submitOnce } from "../utils/mutations";

/** Sends `request` unless one is in flight, then on its answer `done`, or on a refusal `refused`, after the release. */
export type SendOnce = <T>(request: () => Promise<T>, done?: (result: T) => void, refused?: () => void) => void;

/**
 * A modal's sender: one latch for every request it sends, so neither of two actions (a payment and a write-off, a
 * rename and a delete) starts while the other is in flight. The request runs inside the latch: whatever it sets up
 * first (a held copy of the row) happens only for the click that sends.
 */
export function useSubmitOnce(): SendOnce {
  const latch = useRef(false);
  return (request, done, refused) =>
    submitOnce(latch, (release) =>
      void request().then(
        (result) => {
          release();
          done?.(result);
        },
        () => {
          release();
          refused?.();
        },
      ),
    );
}
